// Reason: Provides authentication state and logic to the app (login, register, Google login, logout).
import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../utils/supabaseClient";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const session = supabase.auth.getSession().then(({ data }) => {
      setUser(data?.session?.user || null);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });
    return () => {
      listener?.subscription.unsubscribe();
    };
  }, []);

  const login = async ({ email, password }) => {
    setLoading(true);
    try {
      const { error, data } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      // Only allow login if email is confirmed
      if (!data.user?.confirmed_at) {
        throw new Error("Please confirm your email before logging in.");
      }
      setUser(data.user);
    } finally {
      setLoading(false);
    }
  };

  const register = async ({ email, password }) => {
    setLoading(true);
    try {
      const { error, data } = await supabase.auth.signUp({ email, password });
      if (error) throw error;
      // Do not set user if not confirmed
      if (data.user?.confirmed_at) {
        setUser(data.user);
      }
      return data; // So AuthForm can check confirmation status
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
      });
      if (error) throw error;
      // Redirect handled by Supabase
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        googleLogin,
        logout,
        isAuthenticated: !!(user && user.confirmed_at),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
